const prefix = "bmtg:plausible:form:";
const attemptLifetime = 30 * 60 * 1000;

function parseJSON(value) {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function formConfig(config) {
  const props = config?.props;
  if (
    !["generate_lead", "brochure_request"].includes(config?.event) ||
    !/^\d+$/.test(props?.form_id ?? "") ||
    !/^[a-z][a-z0-9_]{0,39}$/.test(props?.form_type ?? "") ||
    props?.form_category !==
      (config.event === "brochure_request" ? "brochure" : "lead") ||
    !/^[a-z]{2}(?:-[a-z]{2})?$/.test(props?.content_language ?? "")
  )
    return null;
  return {
    event: config.event,
    props: {
      form_id: props.form_id,
      form_type: props.form_type,
      form_category: props.form_category,
      content_language: props.content_language,
    },
  };
}

/** No tracker creation, identifiers, field values, query strings or fragments. */
function send(context, name, props) {
  try {
    if (typeof context.plausible !== "function") return false;
    context.plausible(name, {
      props,
      url: context.location.origin + context.location.pathname,
    });
    return true;
  } catch {
    return false;
  }
}

/** Session storage is required: fail closed when one-time delivery is impossible. */
export function createFormTracker(context = globalThis) {
  function read(id) {
    try {
      const record = parseJSON(context.sessionStorage.getItem(prefix + id));
      if (
        !formConfig(record) ||
        record.props.form_id !== id ||
        !/^[a-f0-9]{32}$/.test(record.attempt ?? "") ||
        !Number.isFinite(record.expires) ||
        record.expires <= Date.now()
      )
        return null;
      return record;
    } catch {
      return null;
    }
  }

  return {
    pending: read,
    start(value) {
      const config = formConfig(value);
      if (!config) return null;
      try {
        if (typeof context.plausible !== "function") return null;
        const existing = read(config.props.form_id);
        if (existing) return existing;
        const bytes = context.crypto.getRandomValues(new Uint8Array(16));
        const record = {
          ...config,
          attempt: Array.from(bytes, (byte) =>
            byte.toString(16).padStart(2, "0"),
          ).join(""),
          expires: Date.now() + attemptLifetime,
        };
        // Storage must work before the first event, otherwise reload/AJAX
        // deduplication cannot be guaranteed. Never fall back to a cookie.
        context.sessionStorage.setItem(
          prefix + config.props.form_id,
          JSON.stringify(record),
        );
        if (!send(context, "form_start", config.props)) {
          context.sessionStorage.removeItem(prefix + config.props.form_id);
          return null;
        }
        return record;
      } catch {
        return null;
      }
    },
    complete(receipt) {
      if (
        !receipt ||
        !/^\d+$/.test(receipt.form_id ?? "") ||
        !Number.isFinite(receipt.expires) ||
        receipt.expires * 1000 < Date.now() ||
        receipt.expires * 1000 > Date.now() + 5 * 60 * 1000 + 10000
      )
        return false;
      const record = read(receipt.form_id);
      if (!record || receipt.attempt !== record.attempt) return false;
      try {
        // Consume before sending: simultaneous hooks and restored confirmation
        // markup cannot count the same attempt twice. Never send the attempt.
        context.sessionStorage.removeItem(prefix + receipt.form_id);
      } catch {
        return false;
      }
      return send(context, record.event, formConfig(record).props);
    },
  };
}

/** The early synchronous head script cleaned the URL before tracker startup. */
export function takeRedirectReceipt(context = globalThis) {
  const receipt = context.bmtgPlausibleFormReceipt;
  delete context.bmtgPlausibleFormReceipt;
  return receipt ?? null;
}

export function initPlausibleForms(context = globalThis) {
  const document = context.document;
  if (!document || context.bmtgPlausibleFormsInitialized) return;
  // Legacy GF AJAX loads the response in a hidden iframe before transferring
  // its markup into the parent. Only the visible document consumes a receipt.
  if (context.frameElement?.name?.startsWith("gform_ajax_frame_")) return;
  context.bmtgPlausibleFormsInitialized = true;
  const tracker = createFormTracker(context);
  const redirectReceipt = takeRedirectReceipt(context);

  function prepare(form, record) {
    if (!record) return;
    let hidden = form.querySelector('input[name="bmtg_plausible_attempt"]');
    if (!hidden) {
      hidden = document.createElement("input");
      hidden.type = "hidden";
      hidden.name = "bmtg_plausible_attempt";
      form.append(hidden);
    }
    hidden.value = record.attempt;
  }

  function scan(root) {
    if (!root.querySelectorAll) return;
    const forms = root.matches?.("form[data-bmtg-plausible-form]")
      ? [root]
      : [];
    forms.push(...root.querySelectorAll("form[data-bmtg-plausible-form]"));
    for (const form of forms) {
      const config = formConfig(parseJSON(form.dataset.bmtgPlausibleForm));
      if (config) prepare(form, tracker.pending(config.props.form_id));
    }
    const receipts = root.matches?.("[data-bmtg-form-success]") ? [root] : [];
    receipts.push(...root.querySelectorAll("[data-bmtg-form-success]"));
    for (const marker of receipts) {
      tracker.complete(parseJSON(marker.dataset.bmtgFormSuccess));
      marker.remove();
    }
  }

  function boot() {
    // WordPress excludes admin assets server-side; this additionally protects
    // logged-in visitors if a cached page unexpectedly includes the asset.
    if (document.body?.classList.contains("logged-in")) return;
    if (redirectReceipt) tracker.complete(redirectReceipt);
    scan(document);

    const interact = (event) => {
      if (!event.isTrusted) return;
      const field = event.target;
      if (
        !field?.matches?.(
          "input:not([type=hidden]):not([type=submit]):not([type=button]), textarea, select",
        )
      )
        return;
      const form = field.closest("form[data-bmtg-plausible-form]");
      if (!form) return;
      prepare(form, tracker.start(parseJSON(form.dataset.bmtgPlausibleForm)));
    };
    // Delegate to survive multi-page and AJAX validation re-renders. An actual
    // edit/selection starts the funnel; mere focus does not.
    document.addEventListener("input", interact, true);
    document.addEventListener("change", interact, true);
    document.addEventListener(
      "submit",
      (event) => {
        const form = event.target;
        const config = formConfig(parseJSON(form?.dataset?.bmtgPlausibleForm));
        if (config) {
          // A trusted submit is the fallback for prefilled/autofilled forms.
          // It can start an attempt, but only GF's receipt confirms success.
          const record = event.isTrusted
            ? tracker.start(config)
            : tracker.pending(config.props.form_id);
          prepare(form, record);
        }
      },
      true,
    );
    const observer = new context.MutationObserver((mutations) => {
      for (const mutation of mutations)
        for (const node of mutation.addedNodes) scan(node);
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === "loading")
    document.addEventListener("DOMContentLoaded", boot, { once: true });
  else boot();
}
